// Cantidad de imágenes del modo "Generar imagen personalizada"
// (kind `design-modelo`), ADR-2026-07-30-imagen-personalizada-cantidad.
//
// Con UNA sola pose (o pose Automática) el backend genera
// `cantidad_imagenes` variaciones con el MISMO prompt: en
// `atlas-backend/src/workers/handlers/generateAd.ts` la ola 1 es
// `wave1Count = multiPose ? 1 : data.cantidad_imagenes`, y `multiPose` es
// `poses.length + customs.length > 1`. Por eso con 2+ poses la cantidad se
// ignora (una imagen por pose, recomposición en la ola 2) y con "varios
// modelos" tampoco aplica (ese flow entrega UNA composición grupal —
// `modelo_mode: 'multiple'`, ADR-2026-08-23-migrar-modelo-mode-multiple).
//
// El estado crudo del contador es el mismo `autoImageCount` que usan
// design-auto y anuncio (ver InterfazV2). Este helper lo traduce a la
// cantidad EFECTIVA para que el payload, el pill de créditos y la UI no se
// desincronicen: cuando el contador no aplica, la cantidad vuelve a 1 sin
// tocar el estado guardado.

/**
 * Tope de modelos elegibles a la vez, en cualquier picker del modo.
 *
 * Tres, porque la tabla `poses` tiene poses de 1, 2 y 3 personas y no más: con
 * cuatro modelos el picker de poses quedaba literalmente vacío. Vive acá y no en
 * un picker para que el tope sea UNO solo — lo miran la grilla multi-select (que
 * apaga las cards al llegar), el handler que escribe el estado y el título del
 * panel (ADR-2026-08-25-tres-modelos-como-maximo).
 */
import { palabraDeModeloActual } from '../../lib/nombreDeModelo';

export const MAX_MODELOS_SERIE = 3;

export interface DesignModeloCountInput {
  /** Valor crudo del contador (`autoImageCount`). */
  rawCount: number;
  /** Poses seleccionadas. 0 = pose Automática (sigue siendo 1 generación). */
  poseCount: number;
  /** Modo "varios modelos" activo. */
  multiModelEnabled: boolean;
  /**
   * Modelos de la serie (grilla multi-select de design-modelo): cada uno se
   * genera por separado, así que con 2+ el contador tampoco aplica — la
   * cantidad la fija la serie, igual que con 2+ poses. Default 1 para los
   * call-sites que no eligen modelos.
   */
  modelCount?: number;
  /** Tope del contador (`AUTO_IMAGE_COUNT_MAX`). */
  max: number;
}

export interface DesignModeloCountResult {
  /** Si el contador se puede usar (y por lo tanto mostrar). */
  enabled: boolean;
  /** Cantidad de imágenes que realmente se van a pedir. */
  count: number;
}

export function resolveDesignModeloCount({
  rawCount,
  poseCount,
  multiModelEnabled,
  modelCount = 1,
  max,
}: DesignModeloCountInput): DesignModeloCountResult {
  const enabled = poseCount <= 1 && !multiModelEnabled && modelCount <= 1;
  if (!enabled) return { enabled: false, count: 1 };
  const count = Math.max(1, Math.min(max, Math.trunc(rawCount) || 1));
  return { enabled: true, count };
}

/**
 * Resumen de la tanda para mostrar arriba del botón Generar: cuántas imágenes
 * salen y de qué combinación. Vive acá, al lado del helper de la cantidad,
 * porque las dos cosas responden a la misma pregunta y tienen que decir lo
 * mismo — el panel mobile y el `configure` a dos columnas lo renderizan en
 * botones distintos (ADR-2026-08-20-modelos-multi-select-serie-3).
 *
 * Con 1 modelo y 1 pose la cantidad la fija el contador y las imágenes son
 * variaciones del mismo par: ahí decir "1 modelo × 1 pose" al lado de
 * "4 imágenes" se leería como un error de cuenta.
 */
export function totalTandaDesignModelo({
  modelCount,
  poseCount,
  count,
}: {
  modelCount: number;
  poseCount: number;
  count: number;
}): number {
  // Con 2+ modelos o 2+ poses el contador ya vale 1 (`resolveDesignModeloCount`),
  // así que multiplicar las tres cosas da bien en los tres casos.
  return Math.max(1, modelCount) * Math.max(1, poseCount) * Math.max(1, count);
}

export function resumenTandaDesignModelo({
  modelCount,
  poseCount,
  count,
}: {
  /** Modelos de la serie (1 = selección única). */
  modelCount: number;
  /** Poses elegidas. 0 = pose Automática, cuenta como 1. */
  poseCount: number;
  /** Cantidad efectiva del contador (`resolveDesignModeloCount().count`). */
  count: number;
}): string {
  if (count > 1) {
    return `${count} imágenes · variaciones del mismo ${palabraDeModeloActual().singular} y pose`;
  }
  const modelos = Math.max(1, modelCount);
  const poses = Math.max(1, poseCount);
  const total = totalTandaDesignModelo({ modelCount, poseCount, count });
  return `${total} ${total === 1 ? 'imagen' : 'imágenes'} · ${modelos} ${
    modelos === 1 ? 'modelo' : 'modelos'
  } × ${poses} ${poses === 1 ? 'pose' : 'poses'}`;
}

/**
 * La tanda de MODELO V2, donde la cantidad del stepper SIEMPRE aplica.
 *
 * En el resto de las variantes el contador se apaga cuando hay 2+ poses o 2+ modelos
 * (`resolveDesignModeloCount`), porque el backend ignora `cantidad_imagenes` en esos
 * casos. En v2 el stepper vive pegado al Generar y está SIEMPRE a la vista: apagarlo por
 * dentro dejaba el número diciendo 3 mientras el botón decía "Generar 1 imagen" y se
 * cobraban 3 créditos — tres cifras distintas para la misma tanda. Acá la cantidad
 * multiplica de verdad, y lo que el backend no sabe hacer lo resuelve el fan-out del
 * front (ADR-2026-08-28-modelo-v2-la-cantidad-manda).
 *
 * `grupal` = composición con varios modelos (`modelo_mode: 'multiple'`): ahí los modelos
 * NO multiplican, salen todos en la misma foto
 * (ADR-2026-08-25-en-v2-los-modelos-van-en-la-misma-foto). Fuera del grupal, cada modelo
 * de la serie es su propia imagen.
 */
export interface TandaModeloInput {
  /** Valor crudo del contador (`autoImageCount`). */
  rawCount: number;
  /** Poses elegidas. 0 = pose Automática, que sigue siendo una. */
  poseCount: number;
  /** Modelos elegidos (1 = selección única). */
  modelCount: number;
  /** Composición grupal: los modelos van todos en la misma foto. */
  grupal: boolean;
  /** Tope del contador (`AUTO_IMAGE_COUNT_MAX`). */
  max: number;
}

export interface TandaModelo {
  /**
   * ¿El stepper de cantidad APLICA en esta combinación?
   *
   * `false` con 2+ poses: ahí la tanda la fija el catálogo de poses —una imagen por
   * pose— y el stepper deja de tener con qué multiplicar
   * (ADR-2026-09-04-con-varias-poses-el-contador-se-esconde). Quien lo dibuje tiene que
   * esconderlo cuando esto es `false`: dejarlo a la vista diciendo 3 mientras el botón
   * dice "Generar 4 imágenes" es la incoherencia que arregló
   * ADR-2026-08-28-modelo-v2-la-cantidad-manda, sólo que del otro lado.
   *
   * Los MODELOS no lo apagan: en la foto grupal salen todos juntos y el stepper sigue
   * pidiendo N variaciones de esa foto, y en la serie el fan-out del front repite el
   * request por modelo.
   */
  contadorAplica: boolean;
  /** Cantidad pedida en el stepper, acotada al tope. 1 cuando el contador no aplica. */
  count: number;
  /** Poses efectivas (mínimo 1: sin elegir ninguna, la pose la decide el modo). */
  poses: number;
  /** Modelos que MULTIPLICAN imágenes (1 en la composición grupal). */
  modelos: number;
  /** Imágenes que van a salir. Es lo que dice el botón. */
  total: number;
  /**
   * Modelos que van JUNTOS en cada foto — el largo de la selección en la composición
   * grupal, 1 fuera de ella. No multiplica imágenes (por eso no entra en `total`), pero
   * sí créditos: ver `unidadesCobradas`.
   */
  modelosPorFoto: number;
  /**
   * Lo que se COBRA, en imágenes-equivalente.
   *
   * Sale separado de `total` porque la cuenta del precio y la de las imágenes pueden
   * dejar de coincidir en la composición grupal — ver
   * `BACKEND_COBRA_CADA_MODELO_DEL_GRUPO`, que es quien decide si hoy coinciden. Este
   * campo es el ÚNICO lugar del front que puede decir un precio de esta tanda: lo usan
   * el pill del Generar y el gate de saldo.
   *
   * La Calidad multiplica DESPUÉS, sobre este número y no sobre `total`.
   */
  unidadesCobradas: number;
}

/**
 * ¿El backend cobra CADA modelo de la composición grupal?
 *
 * **Hoy no, y por eso vale `false`.** El débito lo hace el backend por imagen ENTREGADA
 * (`expectedDeliveredCount` + `debitDelivered`, espejado en `lib/costoGeneracion.ts`), y
 * para `modelo_mode: 'multiple'` esa cuenta da 1: los individuales de la ola 1 salen con
 * `active=false` y explícitamente SIN cobro, y sólo se cobra la composición entregada —
 * ADR-2026-08-23-migrar-modelo-mode-multiple, que además descartó cobrar N+1 como hacía
 * n8n.
 *
 * Poner acá `total × modelosPorFoto` mientras el backend cobra 1 no encarece nada: cobra
 * el backend. Lo que hace es (a) anunciar un precio que después no se debita y (b) —lo
 * grave— frenar en el gate de saldo (`checkGenerationGuards`) a usuarios que SÍ tienen
 * créditos para lo que el backend les va a cobrar. Por eso la multiplicación espera acá,
 * en una línea, hasta que el backend cobre por modelo:
 * ADR-2026-09-04-el-precio-del-grupo-espera-al-backend.
 *
 * Se tipa como `boolean` a propósito (y no se deja inferir `false`): el día que cambie no
 * tiene que pelearse con el narrowing de la rama que hoy no corre.
 */
const BACKEND_COBRA_CADA_MODELO_DEL_GRUPO: boolean = false;

export function resolveTandaModelo({
  rawCount,
  poseCount,
  modelCount,
  grupal,
  max,
}: TandaModeloInput): TandaModelo {
  const poses = Math.max(1, poseCount);
  // Con 2+ poses el contador no aplica y la cantidad vuelve a 1 SIN tocar el estado
  // guardado del stepper: al volver a una sola pose, el número que el usuario había
  // puesto sigue ahí (mismo criterio que `resolveDesignModeloCount`).
  const contadorAplica = poses <= 1;
  const count = contadorAplica
    ? Math.max(1, Math.min(max, Math.trunc(rawCount) || 1))
    : 1;
  const modelos = grupal ? 1 : Math.max(1, modelCount);
  const modelosPorFoto = grupal ? Math.max(1, modelCount) : 1;
  const total = count * poses * modelos;
  return {
    contadorAplica,
    count,
    poses,
    modelos,
    total,
    modelosPorFoto,
    // El precio sale de las imágenes entregadas mientras el backend cobre por entrega:
    // ver `BACKEND_COBRA_CADA_MODELO_DEL_GRUPO`. Fuera del grupal `modelosPorFoto` es 1,
    // así que las dos ramas dan lo mismo y el resto de los caminos no depende del flag.
    unidadesCobradas: BACKEND_COBRA_CADA_MODELO_DEL_GRUPO
      ? total * modelosPorFoto
      : total,
  };
}

/**
 * Resumen de la tanda de v2, en una línea: el total y de dónde sale.
 *
 * Nombra sólo los ejes que multiplican —con un modelo y una pose, decir "1 modelo × 1
 * pose" al lado de "3 imágenes" se lee como un error de cuenta—, y en la composición
 * grupal dice que los modelos van juntos, que es lo que explica por qué tres modelos no
 * son tres imágenes.
 */
export function resumenTandaModelo(
  t: TandaModelo,
  grupal: boolean,
): string {
  const cabecera = `${t.total} ${t.total === 1 ? 'imagen' : 'imágenes'}`;
  const ejes: string[] = [];
  if (t.modelos > 1)
    ejes.push(`${t.modelos} ${palabraDeModeloActual().plural}`);
  if (t.poses > 1) ejes.push(`${t.poses} poses`);
  if (t.count > 1)
    ejes.push(`${t.count} ${t.count === 1 ? 'variación' : 'variaciones'}`);
  // En el grupal los modelos no aparecen en los ejes (no multiplican imágenes), pero la
  // frase los nombra igual: es lo que explica por qué elegir tres devuelve una sola foto.
  // El "y cada uno se cobra" sólo se dice si el precio efectivamente los cobra —
  // prometerlo mientras el backend debita una sola imagen es cobrar de mentira
  // (ADR-2026-09-04-el-precio-del-grupo-espera-al-backend).
  const grupo =
    grupal && t.modelosPorFoto > 1
      ? ` · ${t.modelosPorFoto} ${palabraDeModeloActual().plural} juntos en cada foto${
          BACKEND_COBRA_CADA_MODELO_DEL_GRUPO ? ', y cada uno se cobra' : ''
        }`
      : grupal
        ? ` · los ${palabraDeModeloActual().plural} van juntos en cada foto`
        : '';
  if (ejes.length === 0) return `${cabecera}${grupo}`;
  return `${cabecera} · ${ejes.join(' × ')}${grupo}`;
}

/**
 * Resumen de la tanda del modo AUTOMÁTICO: cuántas imágenes salen y de qué.
 *
 * Vive acá —y no en la pantalla— porque lo dibujan DOS pies distintos: el del panel de
 * productos en desktop y el del `configure` en mobile
 * (ADR-2026-09-02-automatico-mobile-toma-los-ajustes-del-desktop). Con la frase escrita
 * en cada uno, las dos pantallas se iban a separar en el primer retoque.
 *
 * Las tres frases responden la misma pregunta —qué recibo si aprieto— con lo que cambia
 * en cada caso: sin productos, que falta elegir uno; con uno, que la escena no la elegís
 * vos (que es TODO el modo); con varios, que se combinan en UNA imagen y no sale una por
 * producto.
 */
export function resumenTandaAutomatica({
  imagenes,
  productos,
}: {
  /** Cantidad del contador. */
  imagenes: number;
  /** Productos elegidos para la tanda. */
  productos: number;
}): string {
  if (productos === 0) return 'Elegí un producto para generar.';
  const cuantas = `${imagenes} ${imagenes === 1 ? 'imagen' : 'imágenes'}`;
  if (productos === 1)
    return `${cuantas} del producto elegido — la escena la arma la IA`;
  return `${cuantas} con los ${productos} productos juntos, como un outfit`;
}
